import { FC, useMemo, useState, useCallback, useRef, useEffect } from 'react';
import { Dropdown, Image, Space, Typography } from 'antd';
import { GET_USER_NOTIFICATIONS } from 'gql/notifications/queries';
import { useQuery } from '@apollo/client';
import {
  GetUserNotificationsQuery,
  GetUserNotificationsQuery_getUserNotifications_data_results as NotificationsDataType
} from 'gql/notifications/__generated__/GetUserNotificationsQuery';
import dayjs from 'dayjs';
import { DATE_FORMAT } from 'constants/dates';
import { useTranslation } from 'react-i18next';
import bell from 'assets/images/bell_white.svg';
import empty from 'assets/images/empty_notifications.svg';
import { useAppSelector } from 'store/hooks';
import { selectUserIsLoggedIn } from 'store/user/selectors';

import {
  SectionTitle,
  StyledContainer,
  StyledContent,
  StyledEmpty,
  StyledHeader,
  StyledIcon,
  StyledList,
  TabItem
} from './styled';
import NotificationItem from '../NotificationItem';

const { Title, Text } = Typography;

const PAGE_LIMIT = 20;

const tabItems = [
  {
    id: 'all',
    title: 'all'
  },
  {
    id: 'unread',
    title: 'unread'
  }
];

const Notifications: FC = () => {
  // translations
  const { t } = useTranslation();
  // context
  const isLoggedIn = useAppSelector(selectUserIsLoggedIn);
  // states
  const [selected, setSelected] = useState('all');
  const [page, setPage] = useState(0);
  const [hasUnread, setHasUnread] = useState(false);
  const [open, setOpen] = useState(false);
  // refs
  const contentRef = useRef<HTMLDivElement>(null);
  // graphql
  const { data, loading, refetch, fetchMore } =
    useQuery<GetUserNotificationsQuery>(GET_USER_NOTIFICATIONS, {
      fetchPolicy: 'cache-and-network',
      nextFetchPolicy: 'cache-first',
      notifyOnNetworkStatusChange: true,
      skip: !isLoggedIn,
      onCompleted(data) {
        setHasUnread(!!data.getUserNotifications?.data.unread_count);
      },
      variables: {
        query: {
          pagination: {
            page: 0,
            limit: PAGE_LIMIT
          },
          read: selected === 'all' ? undefined : 0
        }
      }
    });

  const results = useMemo(
    () => data?.getUserNotifications?.data?.results || [],
    [data?.getUserNotifications?.data?.results]
  );

  const total = data?.getUserNotifications?.data.total || 0;

  useEffect(() => {
    const handleScroll = () => {
      const isAtBottom =
        contentRef.current &&
        contentRef.current.scrollTop + contentRef.current.clientHeight >=
          contentRef.current.scrollHeight;

      if (isAtBottom && !loading && total > results.length) {
        setPage(prev => prev + 1);
      }
    };

    contentRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      contentRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, [loading, results?.length, total]);

  useEffect(() => {
    if (page) {
      fetchMore({
        variables: {
          query: {
            pagination: {
              page,
              limit: PAGE_LIMIT
            }
          }
        },
        updateQuery: (previousResult, { fetchMoreResult }) => {
          const newResult =
            fetchMoreResult.getUserNotifications?.data.results || [];

          if (
            !previousResult.getUserNotifications?.data.results ||
            !newResult
          ) {
            return previousResult;
          }

          return {
            getUserNotifications: {
              ...previousResult.getUserNotifications,
              data: {
                ...previousResult.getUserNotifications.data,
                results: [
                  ...previousResult.getUserNotifications.data.results,
                  ...newResult
                ]
              }
            }
          };
        }
      });
    }
  }, [fetchMore, page]);

  const renderDate = useCallback(
    (date: string) => {
      const today = new Date();
      const yesterday = dayjs().subtract(1, 'day');

      if (date === dayjs(today).format(DATE_FORMAT)) {
        return t('notifications.today');
      }

      if (date === dayjs(yesterday).format(DATE_FORMAT)) {
        return t('notifications.earlier');
      }

      return date;
    },
    [t]
  );

  const sortedData = useMemo(() => {
    const groupedData = results.reduce(
      (acc: { [key: string]: NotificationsDataType[] }, item) => {
        const createdAt = new Date(item.created_at || '');
        const dateKey = dayjs(createdAt).format(
          DATE_FORMAT
        ) as keyof typeof acc;

        if (!acc[dateKey]) {
          acc[dateKey] = [];
        }

        acc[dateKey]?.push(item);

        return acc;
      },
      {}
    );

    const resultArray = Object.entries(groupedData).map(([date, data]) => {
      return {
        title: renderDate(date),
        data: data as ReadonlyArray<NotificationsDataType>
      };
    });

    return resultArray;
  }, [renderDate, results]);

  const onOpenChange = (open: boolean) => {
    setOpen(open);

    if (open) {
      refetch();
    }
  };

  const handleMenuClick = () => {
    setOpen(false);
  };

  return (
    <Dropdown
      onOpenChange={onOpenChange}
      open={open}
      dropdownRender={() => (
        <StyledContainer>
          <StyledContent>
            <StyledHeader>
              <Title level={4} style={{ margin: 0 }}>
                {t('notifications.title')}
              </Title>
              <Space direction="horizontal" size={6}>
                {tabItems.map(item => (
                  <TabItem
                    selected={selected === item.id}
                    key={item.id}
                    onClick={() => setSelected(item.id)}
                  >
                    <Text>{t(`notifications.${item.title}`)}</Text>
                  </TabItem>
                ))}
              </Space>
            </StyledHeader>
            {sortedData.length ? (
              <StyledList ref={contentRef}>
                {sortedData.map((item, index) => (
                  <Space
                    key={`${item.title}-${index}`}
                    direction="vertical"
                    style={{ width: '100%' }}
                  >
                    <div>
                      <SectionTitle>{item.title}</SectionTitle>
                      {item.data.map((notification, i) => (
                        <NotificationItem
                          key={`${i}-${notification.id}`}
                          onClick={handleMenuClick}
                          id={notification.id}
                        />
                      ))}
                    </div>
                  </Space>
                ))}
              </StyledList>
            ) : (
              <StyledEmpty
                image={
                  <Image src={empty} preview={false} width={160} height={120} />
                }
                description={t(
                  `notifications.${
                    selected === 'all'
                      ? 'no_notifications'
                      : 'no_unread_notifications'
                  }`
                )}
              />
            )}
          </StyledContent>
        </StyledContainer>
      )}
      placement="bottomRight"
      trigger={['click']}
    >
      <StyledIcon $showDot={hasUnread}>
        <Image src={bell} width={18} alt="employ" preview={false} />
      </StyledIcon>
    </Dropdown>
  );
};

export default Notifications;
